import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { Calendar as CalendarIcon, RefreshCw, MapPin, Trophy, Clock, Globe, Ban } from 'lucide-react';
import { toast } from 'sonner';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { useAuth } from '../contexts/AuthContext';
import { useMasterData } from '../contexts/MasterDataContext';
import { API } from '../utils/apiConfig';

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'custom', label: 'Custom' },
];

const TIME_OF_DAY_PRESETS = [
  { value: 'all', label: 'All Time (6 AM – 1 AM)' },
  { value: 'morning', label: 'Morning (6 AM – 12 PM)' },
  { value: 'afternoon', label: 'Afternoon (12 PM – 5 PM)' },
  { value: 'evening', label: 'Evening (5 PM – 9 PM)' },
  { value: 'late_night', label: 'Late Night (9 PM – 1 AM)' },
  { value: '4pm_12am', label: 'Evening Peak (4 PM – 12 AM)' },
  { value: '6pm_12am', label: 'Night (6 PM – 12 AM)' },
];

const PLATFORMS = [
  'Calling',
  'Khelomore',
  'District',
  'Hudle',
  'Walk-in',
  'WhatsApp',
  'Online',
  'App',
  'Others',
];

const STATUS_STYLES = {
  vacant: {
    bg: 'bg-emerald-500',
    label: 'Vacant',
    legend: 'bg-emerald-500',
  },
  booked: {
    bg: 'bg-rose-500',
    label: 'Booked',
    legend: 'bg-rose-500',
  },
  bulk: {
    bg: 'bg-sky-500',
    label: 'Bulk Booking',
    legend: 'bg-sky-500',
  },
  blocked: {
    bg: 'bg-amber-500',
    label: 'Blocked',
    legend: 'bg-amber-500',
  },
  maintenance: {
    bg: 'bg-slate-900',
    label: 'Maintenance (Closed)',
    legend: 'bg-slate-900',
  },
  non_working: {
    bg: 'bg-black',
    label: 'Non-working hour',
    legend: 'bg-black',
  },
};

function resolvePresetDate(preset) {
  if (preset === 'today') return format(new Date(), 'yyyy-MM-dd');
  if (preset === 'yesterday') return format(subDays(new Date(), 1), 'yyyy-MM-dd');
  return format(new Date(), 'yyyy-MM-dd');
}

// Format a single delta line as plain text e.g., "vs yesterday: -12%" or "vs yesterday: —"
function formatDeltaText(label, pct) {
  if (pct === null || pct === undefined) return `vs ${label}: —`;
  const sign = pct > 0 ? '+' : '';
  return `vs ${label}: ${sign}${pct}%`;
}

function deltaToneClass(pct) {
  if (pct === null || pct === undefined) return 'text-slate-400';
  if (pct > 0) return 'text-emerald-600';
  if (pct < 0) return 'text-rose-600';
  return 'text-slate-500';
}

// Renders two plain-text comparison lines (DoD + WoW) under a stat card.
// `field` is 'business_amount' or 'booking_count'; `testidPrefix` becomes e.g. 'business-amount'.
function renderDeltaLines(comparison, field, testidPrefix) {
  if (!comparison) return null;
  const dod = comparison.yesterday?.[`${field}_delta_pct`];
  const wow = comparison.last_week?.[`${field}_delta_pct`];
  return (
    <div className="mt-2 space-y-0.5" data-testid={`${testidPrefix}-comparison`}>
      <p
        className={`text-xs font-medium ${deltaToneClass(dod)}`}
        data-testid={`${testidPrefix}-dod`}
      >
        {formatDeltaText('yesterday', dod)}
      </p>
      <p
        className={`text-xs font-medium ${deltaToneClass(wow)}`}
        data-testid={`${testidPrefix}-wow`}
      >
        {formatDeltaText('last week', wow)}
      </p>
    </div>
  );
}

const OccupancyPage = () => {
  const { token } = useAuth();
  const { venues, getGamesForVenue, getVenueNames } = useMasterData();

  const [datePreset, setDatePreset] = useState('today');
  const [customDate, setCustomDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedVenue, setSelectedVenue] = useState('');
  const [selectedGame, setSelectedGame] = useState('all');
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Active date used by the API
  const activeDate = datePreset === 'custom' ? customDate : resolvePresetDate(datePreset);

  // Default the venue to the first active one once master data is ready
  useEffect(() => {
    if (!selectedVenue) {
      const names = getVenueNames();
      if (names.length > 0) setSelectedVenue(names[0]);
    }
  }, [getVenueNames, selectedVenue]);

  const games = useMemo(() => {
    if (!selectedVenue) return [];
    return getGamesForVenue(selectedVenue);
  }, [selectedVenue, getGamesForVenue]);

  // Reset game when venue changes (and the previously-selected game is unavailable)
  useEffect(() => {
    if (selectedGame !== 'all' && !games.includes(selectedGame)) {
      setSelectedGame('all');
    }
  }, [games, selectedGame]);

  const fetchOccupancy = useCallback(async () => {
    if (!selectedVenue || !token) return;
    setLoading(true);
    try {
      const params = { date: activeDate, venue: selectedVenue, time_of_day: selectedTimeOfDay };
      if (selectedGame && selectedGame !== 'all') params.game = selectedGame;
      if (selectedPlatform && selectedPlatform !== 'all') params.platform = selectedPlatform;
      const res = await axios.get(`${API}/occupancy`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Occupancy fetch error', err);
      toast.error('Failed to load occupancy data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeDate, selectedVenue, selectedGame, selectedTimeOfDay, selectedPlatform, token]);

  useEffect(() => {
    fetchOccupancy();
  }, [fetchOccupancy]);

  const headerDateLabel = useMemo(() => {
    try {
      return format(new Date(activeDate), 'd MMM yyyy');
    } catch {
      return activeDate;
    }
  }, [activeDate]);

  const timeRangeLabel = useMemo(() => {
    if (!data?.hours?.length) return '';
    return `${data.hours[0]} – ${data.hours[data.hours.length - 1]}`;
  }, [data]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="occupancy-page">
        {/* Page header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
              MAKS Court Occupancy
            </h1>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              {headerDateLabel}
              {timeRangeLabel && (
                <>
                  <span className="text-slate-300">|</span>
                  <span>{timeRangeLabel}</span>
                </>
              )}
            </p>
          </div>
          <Button
            onClick={fetchOccupancy}
            disabled={loading}
            variant="outline"
            data-testid="occupancy-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Date preset */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Date Range
              </label>
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger data-testid="occupancy-date-preset">
                  <SelectValue placeholder="Select date" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom date */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                {datePreset === 'custom' ? 'Pick Date' : 'Active Date'}
              </label>
              <Input
                type="date"
                value={datePreset === 'custom' ? customDate : activeDate}
                onChange={(e) => {
                  setCustomDate(e.target.value);
                  setDatePreset('custom');
                }}
                data-testid="occupancy-custom-date"
              />
            </div>

            {/* Time of day */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block flex items-center gap-1">
                <Clock className="w-3 h-3" /> Time of Day
              </label>
              <Select value={selectedTimeOfDay} onValueChange={setSelectedTimeOfDay}>
                <SelectTrigger data-testid="occupancy-time-of-day-select">
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_OF_DAY_PRESETS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Venue */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Venue
              </label>
              <Select value={selectedVenue} onValueChange={setSelectedVenue}>
                <SelectTrigger data-testid="occupancy-venue-select">
                  <SelectValue placeholder="Select venue" />
                </SelectTrigger>
                <SelectContent>
                  {venues.filter((v) => v.is_active).map((v) => (
                    <SelectItem key={v.id} value={v.name}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Game */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block flex items-center gap-1">
                <Trophy className="w-3 h-3" /> Game
              </label>
              <Select value={selectedGame} onValueChange={setSelectedGame}>
                <SelectTrigger data-testid="occupancy-game-select">
                  <SelectValue placeholder="All Games" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Games</SelectItem>
                  {games.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Platform */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block flex items-center gap-1">
                <Globe className="w-3 h-3" /> Platform
              </label>
              <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                <SelectTrigger data-testid="occupancy-platform-select">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Legend */}
        <div className="flex items-center gap-6 flex-wrap text-sm">
          {Object.entries(STATUS_STYLES).map(([key, s]) => (
            <div key={key} className="flex items-center gap-2" data-testid={`legend-${key}`}>
              <span className={`w-4 h-4 rounded-sm ${s.legend}`} />
              <span className="text-slate-700 font-medium">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Gamewise Free Slots */}
        {data && data.stats?.gamewise_free_slots && Object.keys(data.stats.gamewise_free_slots).length > 0 && (
          <div data-testid="gamewise-free-slots-section">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-semibold">
              Available Free Slots by Game{timeRangeLabel ? ` (${timeRangeLabel})` : ''}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {Object.entries(data.stats.gamewise_free_slots)
                .sort((a, b) => b[1] - a[1])
                .map(([gameName, freeCount]) => (
                  <Card
                    key={gameName}
                    className="p-3 border-emerald-200 bg-emerald-50/50"
                    data-testid={`free-slot-card-${gameName}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy className="w-3.5 h-3.5 text-emerald-700" />
                      <p className="text-xs font-medium text-slate-700 truncate" title={gameName}>
                        {gameName}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700 leading-tight">
                      {freeCount}
                    </p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">
                      Free Slot{freeCount !== 1 ? 's' : ''}
                    </p>
                  </Card>
                ))}
            </div>
          </div>
        )}

        {/* Grid */}
        <Card className="overflow-hidden" data-testid="occupancy-grid-card">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading occupancy…</div>
          ) : !data || !data.courts.length ? (
            <div className="p-12 text-center text-slate-500">
              No courts found for the selected filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="occupancy-grid-table">
                <thead>
                  <tr className="bg-slate-900 text-white text-xs">
                    <th className="px-3 py-3 text-left font-semibold sticky left-0 z-10 bg-slate-900 min-w-[110px]">
                      COURT
                    </th>
                    {data.hours.map((h) => (
                      <th key={h} className="px-2 py-3 text-center font-medium min-w-[52px]">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.courts.map((row) => (
                    <tr key={row.court_id} className="border-b border-slate-100">
                      <td
                        className="px-3 py-2 font-medium text-slate-800 bg-slate-50 sticky left-0 z-10 text-sm"
                        data-testid={`court-row-${row.court}`}
                      >
                        {row.court}
                      </td>
                      {row.cells.map((cell, idx) => {
                        const style = STATUS_STYLES[cell.status] || STATUS_STYLES.vacant;
                        return (
                          <td key={idx} className="p-0.5 align-middle">
                            {cell.tooltip ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className={`h-8 w-full rounded-sm ${style.bg} cursor-pointer hover:opacity-80 active:opacity-70 transition-opacity touch-manipulation focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-400`}
                                    data-testid={`cell-${row.court}-${cell.hour}`}
                                    data-status={cell.status}
                                    aria-label={`${cell.status} cell at ${cell.label}`}
                                  />
                                </PopoverTrigger>
                                <PopoverContent
                                  side="top"
                                  align="center"
                                  className="max-w-xs p-3 bg-slate-900 text-white border-slate-800"
                                >
                                  <div className="space-y-1">
                                    <p className="font-semibold text-sm">{cell.tooltip.title}</p>
                                    {cell.tooltip.subtitle && (
                                      <p className="text-xs text-slate-300">
                                        {cell.tooltip.subtitle}
                                      </p>
                                    )}
                                    <div className="flex items-center gap-2 pt-1">
                                      <span className="text-[10px] uppercase tracking-wider text-slate-400">
                                        Platform
                                      </span>
                                      <span className="text-xs font-medium">
                                        {cell.tooltip.platform || '—'}
                                      </span>
                                    </div>
                                    {cell.tooltip.booking_id && (
                                      <p className="text-[10px] text-slate-400 pt-0.5">
                                        ID: {cell.tooltip.booking_id}
                                      </p>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <div
                                className={`h-8 w-full rounded-sm ${style.bg}`}
                                data-testid={`cell-${row.court}-${cell.hour}`}
                                data-status={cell.status}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Stats footer */}
        {data && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            data-testid="occupancy-stats-footer"
          >
            <Card className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">Bookings</p>
              <p
                className="text-2xl font-bold text-slate-900 mt-1"
                data-testid="stat-booking-count"
              >
                {data.stats.booking_count ?? 0}
              </p>
              {renderDeltaLines(data.stats.comparison, 'booking_count', 'booking-count')}
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">Peak Hours</p>
              <p
                className="text-2xl font-bold text-slate-900 mt-1"
                data-testid="stat-peak-hours"
              >
                {data.stats.peak_hours}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">Occupancy Rate</p>
              <p
                className="text-2xl font-bold text-emerald-600 mt-1"
                data-testid="stat-occupancy-rate"
              >
                {data.stats.occupancy_rate}%
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">Business Amount</p>
              <p
                className="text-2xl font-bold text-slate-900 mt-1"
                data-testid="stat-business-amount"
              >
                ₹{(data.stats.business_amount ?? 0).toLocaleString('en-IN')}
              </p>
              {renderDeltaLines(data.stats.comparison, 'business_amount', 'business-amount')}
            </Card>
          </div>
        )}

        {/* Bulk Booked / Blocked Slots table */}
        {data && (
          <Card className="overflow-hidden" data-testid="bulk-booked-slots-card">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
              <Ban className="w-4 h-4 text-rose-600" />
              <h3 className="font-semibold text-slate-900 text-sm">
                Bulk Booked Slots
                <span className="ml-2 text-xs font-normal text-slate-500">
                  active on {data.date}{data.venue ? ` · ${data.venue}` : ''}{data.game ? ` · ${data.game}` : ''}
                </span>
              </h3>
            </div>
            {(!data.blocked_slots || data.blocked_slots.length === 0) ? (
              <div className="p-8 text-center text-sm text-slate-400" data-testid="bulk-booked-empty">
                No bulk-booked slots active for the selected filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bulk-booked-slots-table">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Court</th>
                      <th className="text-left px-4 py-2 font-semibold">Time</th>
                      <th className="text-left px-4 py-2 font-semibold">Weekdays</th>
                      <th className="text-left px-4 py-2 font-semibold">Reason</th>
                      <th className="text-left px-4 py-2 font-semibold">Instructed By</th>
                      <th className="text-left px-4 py-2 font-semibold">Period</th>
                      <th className="text-left px-4 py-2 font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.blocked_slots.map((b, i) => (
                      <tr key={b.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`bulk-booked-row-${i}`}>
                        <td className="px-4 py-2 font-medium text-slate-900">{b.court}{b.game ? <span className="text-slate-400 font-normal"> · {b.game}</span> : null}</td>
                        <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{b.start_time} - {b.end_time}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(b.weekday_labels || []).map((w) => (
                              <span key={w} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-medium">{w}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          {String(b.reason || '').toLowerCase() === 'maintenance' ? (
                            <span className="px-2 py-0.5 rounded-full bg-slate-900 text-white text-xs font-medium" data-testid={`maintenance-badge-${i}`}>
                              {b.reason} · Closed
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-xs font-medium">{b.reason}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-slate-600">{b.instructed_by || '-'}</td>
                        <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">
                          {b.continue_monthly ? `From ${b.start_date} · monthly` : `${b.start_date} → ${b.end_date}`}
                        </td>
                        <td className="px-4 py-2 text-slate-500 italic max-w-[220px] truncate" title={b.notes}>{b.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        <p className="text-xs text-slate-400">
          • Each block represents 1 hour. Tap or click any colored cell to see the booking name and platform.
          Bulk bookings are 3 or more consecutive hours by the same booking.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default OccupancyPage;
